import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DataGrid as DxDataGrid, IDataGridOptions } from 'devextreme-react/data-grid';
import { useNavigate } from 'react-router-dom';
import {
  Column,
  ColumnChooser,
  FilterRow,
  HeaderFilter,
  Item,
  NuvoDataGrid,
  Pager,
  Paging,
  Selection,
  Sorting,
  Toolbar,
  StateStoring,
  Lookup,
} from '@nuvolo/nuux-core/components/NuvoDataGrid';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoLink } from '@nuvolo/nuux-core/components/NuvoLink';
import type { ClickEvent } from 'devextreme/ui/button';
import Styles from './Styles';
import type DataGridProps from './DataGridProps';
import ToolbarButton from './ToolbarButton';
import type { ActionFn, CellRenderEvent, ColumnProps, PredefinedAction } from './DataGridProps';
import parseUrl from '../../utils/parsers/parseUrl';
import { isType } from '../../utils/typeGuards';
import useDataStore from '#/common/hooks/useDataStore';
import { EventInfo } from 'devextreme/events';
import dxDataGrid, { SelectionChangedInfo } from 'devextreme/ui/data_grid';
import useSearchQuery from '#/common/hooks/useSearchQuery';
import { clearFilters } from '#/common/utils/clearFilters';
import { get } from 'lodash';
import useStateStoring, { GridState } from './useStateStoring';
import { fixDateTZ, getColumnHeaderFilter } from './utils';
import { useApolloClient } from '@apollo/client';
import { NULL_LABEL, NULL_STR } from '#/config/consts';
import useSession from '#/common/hooks/useSession';
import { isUUID } from '#/common/utils/patterns/uuid';
import { utcToLocalString } from '#/common/utils/datetimeTransform';

const allowedPageSizes: number[] = [5, 10, 15, 25];
const pageSizeThreshold = 10;
const remoteOperations: IDataGridOptions['remoteOperations'] = {
  paging: true,
  filtering: true,
  sorting: true,
};

const isValidDateInput = (val: unknown): val is string | Date | number => {
  if (val === null || val === undefined || val === '') return false;
  if (val instanceof Date) return !isNaN(val.getTime());
  if (typeof val === 'string' && val.trim() === '') return false;
  return true;
};

const createValidDate = (val: unknown): Date | null => {
  if (!isValidDateInput(val)) return null;
  const date = new Date(val as string | number | Date);
  return isNaN(date.getTime()) ? null : date;
};

const DataGrid = <T,>(props: DataGridProps<T>) => {
  const query = useSearchQuery();

  const {
    id,
    keyExpr = 'id' as keyof T & string,
    pluralText = 'Items',
    columns,
    children,
    recordUrl = ':id',
    selectedActions = [],
    actions = [],
    selectionMode = 'multiple',
    getItemsQuery,
    dataSource,
    permanentFilters,
    setRef,
    disablePaging = false,
    disableSorting = false,
    disableFiltering = false,
  } = props;
  const dg = useRef<DxDataGrid>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (dg.current && setRef) {
      setRef(dg.current!);
    }
  }, [dg.current]);

  // Do not show Tenant column if single tenant
  const { tenantIDs } = useSession();
  const filteredColumns = useMemo(
    () =>
      tenantIDs?.length === 1
        ? columns.filter(
            (col) => col.dataField !== 'tenantId' && !col.dataField.startsWith('tenant.'),
          )
        : columns,
    [columns, tenantIDs],
  );

  const gqlClient = useApolloClient();
  const dataStore = getItemsQuery
    ? useDataStore<T>({
        getItemList: getItemsQuery,
        columns: filteredColumns,
        filter: permanentFilters,
      })
    : dataSource || [];
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const hasSelection = selectedItems.length > 0;
  const selectionEnabled = selectedActions.length > 0;

  const showColumnChooser = useCallback(() => {
    dg.current?.instance.showColumnChooser();
  }, []);

  const resetSettings = useCallback(() => {
    dg.current?.instance.state(null);
    dg.current?.instance.clearFilter();
  }, []);

  const removeFilters = useCallback(() => {
    dg.current?.instance.clearFilter();
  }, []);

  const refreshData = useCallback(() => {
    dg.current?.instance.refresh().catch((e) => console.error(e));
  }, []);

  const cellRenderLink = useCallback(
    (c: CellRenderEvent<T>): ReactNode => {
      const editorOptions = c.column.editorOptions as { onClick?: ColumnProps<T>['onClick'] };

      const onClick = () => {
        if (editorOptions?.onClick) {
          editorOptions.onClick({
            row: c.data,
            navigate,
          });
        } else {
          const to = parseUrl(recordUrl, c.data as Record<string, unknown>);
          navigate(to);
        }
      };

      return (
        <NuvoLink type="body" onClick={onClick}>
          {c.displayValue}
        </NuvoLink>
      );
    },
    [navigate, recordUrl],
  );

  const getActionClick = (action: ActionFn<T>) => {
    return (e: ClickEvent, buttonRef?: React.RefObject<HTMLDivElement | null>) => {
      if (!action.onClick) {
        return;
      }

      const result = action.onClick({
        dg: dg.current,
        navigate,
        selectedKeys: selectedItems.map((s: T) => s[keyExpr] as string),
        selectedItems: selectedItems,
        setIsLoading: (val: boolean, message?: string) => {
          if (val) {
            dg.current?.instance.beginCustomLoading(message || '');
            e.component.option('disabled', true);
          } else {
            dg.current?.instance.endCustomLoading();
            e.component.option('disabled', false);
          }
        },
        buttonRef,
      });
    };
  };

  const getActionDisabled = (action: ActionFn<T>, defaultValue: boolean) => {
    if (isType(action.disabled, 'boolean')) {
      return action.disabled;
    }

    if (isType(action.disabled, 'function')) {
      return action.disabled({
        dg: dg.current,
        navigate,
        selectedKeys: selectedItems.map((s: T) => s[keyExpr] as string),
        selectedItems: selectedItems as T[],
      });
    }

    if (!action.onClick) {
      return true;
    }

    return defaultValue;
  };

  const predefinedActions: Record<PredefinedAction, ReactNode> = {
    'column-chooser': (
      <Item key="column-chooser" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Select Columns" icon="table-columns" onClick={showColumnChooser} />
      </Item>
    ),
    'reset-settings': (
      <Item key="reset-settings" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Reset Settings" icon="reset_settings" onClick={resetSettings} />
      </Item>
    ),
    'clear-filters': (
      <Item key="clear-filters" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Clear Filters" icon="filter-slash" onClick={removeFilters} />
      </Item>
    ),
    'refresh-data': (
      <Item key="refresh-data" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Reload data" icon="arrow-rotate-right" onClick={refreshData} />
      </Item>
    ),
  };

  const setSelectedData = useCallback(
    (e: EventInfo<dxDataGrid<unknown, unknown>> & SelectionChangedInfo<unknown, unknown>) => {
      const selectedRowsData = (e.selectedRowsData as T[]) ?? [];

      const transformedRows = selectedRowsData.map((row) => {
        const transformed = { ...row } as Record<string, unknown>;
        filteredColumns
          .map((col) => col.dataField)
          .forEach((fieldName) => {
            if (fieldName in transformed && transformed[fieldName] === NULL_STR) {
              transformed[fieldName] = null;
            }
          });

        return transformed as T;
      });

      setSelectedItems(transformedRows);
    },
    [],
  );

  const initialState: GridState = {};
  if (query.filters.length > 0) {
    const column: any[] = filteredColumns.map((c) => {
      return { dataField: c.dataField, dataType: c.dataType };
    });
    const { filtersClean } = clearFilters(query.filters, column);
    if (filtersClean.length > 0) {
      initialState.filterValue = filtersClean;
    }
  }

  const stateStoring = useStateStoring(id, filteredColumns, initialState);

  const handleOptionChanged = useCallback(
    (e: any) => {
      if (e && e.name === 'filterValue' && e.fullName === 'filterValue') {
        stateStoring.updateFilters(e.value);
      }
    },
    [stateStoring.updateFilters],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (dg.current?.instance) {
        const count = dg.current.instance.totalCount();
        const currentPageSize = dg.current.instance.pageSize();
        if (count !== totalCount) {
          setTotalCount(count);
        }
        if (currentPageSize !== pageSize) {
          setPageSize(currentPageSize);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [totalCount, pageSize]);

  return (
    <Styles>
      <NuvoDataGrid
        ref={dg}
        dataSource={dataStore}
        allowColumnReordering
        allowColumnResizing
        showBorders={false}
        onSelectionChanged={setSelectedData}
        onOptionChanged={handleOptionChanged}
        columnResizingMode="nextColumn"
        columnMinWidth={50}
        remoteOperations={remoteOperations}
        filterSyncEnabled>
        {filteredColumns.map((c) => {
          if (
            isType(c.lookup?.dataSource, 'array') &&
            c.lookup?.nullable &&
            !c.lookup.dataSource.find(
              (item: any) => item[c.lookup?.valueExpr ?? 'value'] === NULL_STR,
            )
          ) {
            c.lookup.dataSource = [
              ...c.lookup.dataSource,
              {
                [c.lookup.valueExpr ?? 'value']: NULL_STR,
                [c.lookup.displayExpr ?? 'text']: NULL_LABEL,
              },
            ];
          }
          return (
            <Column
              key={c.dataField}
              dataField={c.dataField}
              caption={c.caption}
              showInColumnChooser={c.showInColumnChooser}
              dataType={c.dataType ?? 'string'}
              visible={!!c.visible}
              fixed={c.fixed}
              sortIndex={get(c, 'sortIndex')}
              sortOrder={get(c, 'sortOrder')}
              width={c.width || (c.fixed && c.dataField === 'displayNumber' ? 130 : undefined)}
              allowHiding={!c.fixed}
              allowSearch={c.allowSearch ?? true}
              allowFiltering={c.allowFiltering !== undefined ? c.allowFiltering : !c.lookup}
              allowSorting={c.allowSorting ?? true}
              filterType={c.filterType}
              filterValues={c.filterValues}
              calculateSortValue={c.calcSortValue || c.dataField}
              calculateFilterExpression={function (this: any, value: unknown, operation: string) {
                // Handle "between" operation for date, datetime, and numeric columns
                if (operation === 'between') {
                  if (!Array.isArray(value) || value.length === 0) {
                    return undefined;
                  }

                  const [startValue, endValue] = value;

                  // Handle date/datetime between
                  if (c.dataType === 'date' || c.dataType === 'datetime') {
                    const startDate = createValidDate(startValue);
                    const endDate = createValidDate(endValue);

                    if (!startDate && !endDate) {
                      return undefined;
                    }
                    if (c.dataType === 'date') {
                      if (startDate) fixDateTZ(startDate);
                      if (endDate) fixDateTZ(endDate);
                    }

                    // adjust end date to include the entire day (23:59:59.999)
                    if (c.dataType === 'datetime' && endDate) {
                      if (
                        endDate.getHours() === 0 &&
                        endDate.getMinutes() === 0 &&
                        endDate.getSeconds() === 0
                      ) {
                        endDate.setHours(23, 59, 59, 999);
                      }
                    }

                    // Convert to ISO strings for consistent serialization
                    const startISO = startDate ? startDate.toISOString() : null;
                    const endISO = endDate ? endDate.toISOString() : null;

                    // Build filter expression
                    if (startISO && endISO) {
                      return [[c.dataField, '>=', startISO], 'and', [c.dataField, '<=', endISO]];
                    } else if (startISO) {
                      return [c.dataField, '>=', startISO];
                    } else if (endISO) {
                      return [c.dataField, '<=', endISO];
                    }
                    return undefined;
                  }

                  // Handle numeric and other types between
                  const hasStart =
                    startValue !== null && startValue !== undefined && startValue !== '';
                  const hasEnd = endValue !== null && endValue !== undefined && endValue !== '';

                  if (!hasStart && !hasEnd) {
                    return undefined;
                  }

                  if (hasStart && hasEnd) {
                    return [[c.dataField, '>=', startValue], 'and', [c.dataField, '<=', endValue]];
                  } else if (hasStart) {
                    return [c.dataField, '>=', startValue];
                  } else if (hasEnd) {
                    return [c.dataField, '<=', endValue];
                  }
                  return undefined;
                }

                if (c.dataType === 'date' || c.dataType === 'datetime') {
                  const date = new Date(value as string);
                  if (isNaN(date.getTime())) {
                    return this.defaultCalculateFilterExpression.call(
                      this,
                      value,
                      operation,
                      value,
                    );
                  }
                  if (c.dataType === 'date') {
                    fixDateTZ(date);
                  }

                  if (operation === '=') {
                    const end = new Date(date);
                    if (c.dataType === 'date') {
                      end.setUTCDate(date.getUTCDate() + 1);
                    } else {
                      date.setUTCMinutes(date.getUTCMinutes() - 1);
                      end.setUTCMinutes(end.getUTCMinutes() + 1);
                    }

                    return [
                      [c.dataField, '>=', date.toISOString()],
                      'and',
                      [c.dataField, '<', end.toISOString()],
                    ];
                  }

                  return [c.dataField, operation, date.toISOString()];
                }

                // When the column has a filterField, text searches go to that field (e.g. location.name)
                // while ID-based filters (UUIDs, from dashboard navigation or header filter) stay on dataField.
                if (c.filterField) {
                  if (!isUUID(String(value))) {
                    const textOperation = operation === '=' ? 'contains' : operation;
                    return [c.filterField, textOperation, value];
                  }
                }

                return this.defaultCalculateFilterExpression.call(this, value, operation, value);
              }}
              calculateDisplayValue={
                c.calcDisplayValue || c.lookup
                  ? c.calcDisplayValue
                  : (row: T) => {
                      const value = get(row, c.dataField);
                      if (value === NULL_STR) {
                        return '';
                      }
                      if (c.dataType === 'datetime') {
                        return utcToLocalString(value as string);
                      }
                      return value;
                    }
              }
              editorOptions={c.onClick ? { onClick: c.onClick } : undefined}
              cellRender={c.openOnClick ? cellRenderLink : c.cellRender}
              format={c.format}
              allowHeaderFiltering={!!c.headerFilter || !!c.lookup}>
              {c.lookup && <Lookup {...c.lookup} />}
              {(c.headerFilter || c.lookup) && (
                <HeaderFilter allowSearch {...getColumnHeaderFilter(gqlClient, c)} visible={true} />
              )}
            </Column>
          );
        })}

        <Selection mode={selectionMode} allowSelectAll={false} />

        {!disablePaging && <Paging enabled defaultPageSize={10} />}
        {!disablePaging && (
          <Pager
            visible={totalCount > pageSizeThreshold}
            allowedPageSizes={allowedPageSizes}
            displayMode="full"
            showInfo
            showNavigationButtons
            showPageSizeSelector={totalCount > pageSizeThreshold}
          />
        )}

        {!disableSorting && <Sorting mode="single" />}

        {!disableFiltering && <HeaderFilter visible />}
        {!disableFiltering && <FilterRow visible />}

        <Toolbar visible>
          <Item locateInMenu="auto" location="before" visible={selectionEnabled}>
            <NuvoBox>
              Selected {pluralText}: {selectedItems.length}
            </NuvoBox>
          </Item>

          <Item name="groupPanel" location="center" locateInMenu="auto" />

          {actions.map((a) =>
            typeof a === 'string' ? (
              predefinedActions[a]
            ) : (
              <Item key={`action-${a.key}`} locateInMenu="auto" location="after">
                <ToolbarButton
                  tooltip={a.text}
                  icon={a.icon}
                  showTextOnly={a.showTextOnly}
                  onClick={getActionClick(a)}
                  disabled={getActionDisabled(a, false)}
                />
              </Item>
            ),
          )}

          {selectedActions.map((a) => (
            <Item key={a.key} locateInMenu="auto" location="after" visible={totalCount > 0}>
              <ToolbarButton
                tooltip={a.text}
                icon={a.icon}
                onClick={getActionClick(a)}
                disabled={getActionDisabled(a, !hasSelection)}
              />
            </Item>
          ))}

          <Item name="searchPanel" location="after" locateInMenu="auto" />
        </Toolbar>

        <ColumnChooser enabled />
        <StateStoring
          enabled
          type="custom"
          customLoad={stateStoring.load}
          customSave={stateStoring.save}
          savingTimeout={100}
        />

        {children}
      </NuvoDataGrid>
    </Styles>
  );
};

export default DataGrid;
